import type { ExclusionRule, Order, OverrideRecord, SessionMeta } from '../types';
import { db } from './db';

const KEYS = {
  aliases: 'oemstream_user_aliases',
  exclusions: 'oemstream_exclusion_rules',
  deletedEvents: 'oemstream_deleted_event_ids',
  session: 'oemstream_session_meta',
};

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function uid(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Local admin configuration store: user aliases, activity exclusion rules,
 * deleted raw-event ids, and the "selected session" video metadata.
 * Everything applies globally on top of the cached analytics data.
 */
class AdminStore {
  /* ── User aliases (e.g. "10" → "Acme Hospital") ── */

  getAliases(): Record<string, string> {
    return read<Record<string, string>>(KEYS.aliases, {});
  }

  setAlias(userId: string, name: string): void {
    const aliases = this.getAliases();
    const trimmed = name.trim();
    if (trimmed) {
      aliases[userId.trim()] = trimmed;
    } else {
      delete aliases[userId.trim()];
    }
    write(KEYS.aliases, aliases);
  }

  removeAlias(userId: string): void {
    const aliases = this.getAliases();
    delete aliases[userId];
    write(KEYS.aliases, aliases);
  }

  aliasFor(userId: string | number | null | undefined): string | null {
    if (userId == null) return null;
    return this.getAliases()[String(userId)] ?? null;
  }

  /* ── Exclusion rules (filter out activity by user id or property) ── */

  getExclusions(): ExclusionRule[] {
    return read<ExclusionRule[]>(KEYS.exclusions, []);
  }

  addExclusion(rule: Omit<ExclusionRule, 'id' | 'createdAt'>): ExclusionRule {
    const rules = this.getExclusions();
    const full: ExclusionRule = { ...rule, id: uid(), createdAt: new Date().toISOString() };
    rules.push(full);
    write(KEYS.exclusions, rules);
    return full;
  }

  removeExclusion(ruleId: string): void {
    write(KEYS.exclusions, this.getExclusions().filter(r => r.id !== ruleId));
  }

  /* ── Deleted raw events ── */

  getDeletedEventIds(): string[] {
    return read<string[]>(KEYS.deletedEvents, []);
  }

  deleteEvent(eventId: string): void {
    const ids = this.getDeletedEventIds();
    if (!ids.includes(eventId)) {
      ids.push(eventId);
      write(KEYS.deletedEvents, ids);
    }
  }

  restoreEvent(eventId: string): void {
    write(KEYS.deletedEvents, this.getDeletedEventIds().filter(id => id !== eventId));
  }

  /* ── Selected session video ── */

  getSession(): SessionMeta {
    return read<SessionMeta>(KEYS.session, { videoId: null, title: '', notes: '' });
  }

  setSession(meta: Partial<SessionMeta>): SessionMeta {
    const next = { ...this.getSession(), ...meta };
    write(KEYS.session, next);
    return next;
  }

  /* ── Import / export (share admin config across instances) ── */

  exportData(): string {
    return JSON.stringify(
      {
        format: 'oemstream-admin-config',
        version: 2,
        exportedAt: new Date().toISOString(),
        aliases: this.getAliases(),
        exclusions: this.getExclusions(),
        deletedEventIds: this.getDeletedEventIds(),
        orders: db.getOverrideOrders(),
        fieldOverrides: db.getOverrides(),
        deletedOrderIds: db.getDeletedIds(),
      },
      null,
      2
    );
  }

  /**
   * Import an admin config bundle.
   *  - merge: imported aliases win on conflict, exclusion rules are appended
   *    (deduped), orders/overrides upserted by id, deleted ids unioned.
   *  - replace: existing config is fully overwritten.
   */
  importData(json: string, mode: 'merge' | 'replace'): { ok: boolean; error?: string; summary?: string } {
    let data: any;
    try {
      data = JSON.parse(json);
    } catch {
      return { ok: false, error: 'Invalid JSON — not a readable config file.' };
    }

    const aliases = data.aliases && typeof data.aliases === 'object' ? data.aliases as Record<string, string> : null;
    const exclusions = Array.isArray(data.exclusions) ? data.exclusions as ExclusionRule[] : null;
    const deletedEventIds = Array.isArray(data.deletedEventIds) ? (data.deletedEventIds as unknown[]).filter(x => typeof x === 'string') as string[] : [];
    const orders = Array.isArray(data.orders) ? (data.orders as Order[]).filter(o => o && typeof o.id === 'string') : [];
    const fieldOverrides = Array.isArray(data.fieldOverrides) ? (data.fieldOverrides as OverrideRecord[]).filter(o => o && typeof o.orderId === 'string') : [];
    const deletedOrderIds = Array.isArray(data.deletedOrderIds) ? (data.deletedOrderIds as unknown[]).filter(x => typeof x === 'string') as string[] : [];

    if (!aliases && !exclusions && orders.length === 0 && fieldOverrides.length === 0) {
      return { ok: false, error: 'No aliases, exclusion rules, or orders found in this file.' };
    }

    const safeExclusions: ExclusionRule[] = (exclusions || [])
      .filter(r => r && typeof r.value === 'string' && (r.kind === 'user' || r.kind === 'property'))
      .map(r => ({
        id: typeof r.id === 'string' ? r.id : uid(),
        kind: r.kind,
        key: typeof r.key === 'string' ? r.key : undefined,
        value: r.value,
        op: r.op === 'contains' ? 'contains' : 'equals',
        note: typeof r.note === 'string' ? r.note : undefined,
        createdAt: typeof r.createdAt === 'string' ? r.createdAt : new Date().toISOString(),
      }));

    if (mode === 'replace') {
      write(KEYS.aliases, aliases || {});
      write(KEYS.exclusions, safeExclusions);
      write(KEYS.deletedEvents, deletedEventIds);
      db.importOrders(orders, 'replace');
      db.importFieldOverrides(fieldOverrides, 'replace');
      db.importDeletedIds(deletedOrderIds, 'replace');
    } else {
      write(KEYS.aliases, { ...this.getAliases(), ...(aliases || {}) });

      const existing = this.getExclusions();
      const seen = new Set(existing.map(r => `${r.kind}|${r.key ?? ''}|${r.op ?? 'equals'}|${r.value}`));
      const merged = [...existing];
      for (const r of safeExclusions) {
        const sig = `${r.kind}|${r.key ?? ''}|${r.op ?? 'equals'}|${r.value}`;
        if (!seen.has(sig)) {
          seen.add(sig);
          merged.push(r);
        }
      }
      write(KEYS.exclusions, merged);

      write(KEYS.deletedEvents, [...new Set([...this.getDeletedEventIds(), ...deletedEventIds])]);
      db.importOrders(orders, 'merge');
      db.importFieldOverrides(fieldOverrides, 'merge');
      db.importDeletedIds(deletedOrderIds, 'merge');
    }

    return {
      ok: true,
      summary: `${Object.keys(aliases || {}).length} aliases, ${safeExclusions.length} filter rules, ${orders.length} orders, ${fieldOverrides.length} field overrides (${mode}d)`,
    };
  }

  clearAll(): void {
    Object.values(KEYS).forEach(k => localStorage.removeItem(k));
  }
}

export const adminStore = new AdminStore();
export default AdminStore;
